// ── V154: 인쇄할 것 고르기 — 레시피 + 단가표를 한 번에, 몇 개는 빼고 ─────────────────
// 태블릿을 못 쓰는 직원용 종이. 고른 것(뺀 것)은 이 기기에 기억해서 다음 인쇄 때 그대로.
import React, { useMemo, useState } from 'react';
import { useLang } from '../i18n/LangContext';
import { buildZhMap } from '../i18n/BiName';
import { printBundle } from '../utils/recipePrint';
import { searchVariants } from '../utils/hangulKeys';
import { normalizeIngredientName } from '../utils/ingredientMatch';

const LS_KEY = 'semfit_print_pick_v1';
const PRICE_CATS = ['신선식품', '공산품', '소모품', '반제품'];
const CAT_ZH = { 신선식품: '生鲜', 공산품: '干货', 소모품: '耗材', 반제품: '半成品' };
export const priceKey = (p) => `${String(p?.code || '').trim()}|${String(p?.name || '').trim()}|${String(p?.vendor || '').trim()}`;

function readPrefs() {
  try { return JSON.parse(localStorage.getItem(LS_KEY) || 'null') || null; } catch (e) { return null; }
}
function writePrefs(v) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(v)); } catch (e) { /* 무시 */ }
}

export default function PrintPicker({ categories = [], prices = [], onClose, initial = { recipes: true, prices: true }, isMobile = false }) {
  const { lang, fmt: money } = useLang();
  const zh = lang === 'zh';
  const Z = (ko, z) => (zh ? z : ko);
  const recipes = useMemo(() => (categories || []).flatMap(c => (c && c.items) || []).filter(r => r && r.id && r.name), [categories]);
  const rows = useMemo(() => (prices || []).filter(p => p && String(p.name || '').trim()), [prices]);
  const zhMap = useMemo(() => buildZhMap(prices || [], recipes), [prices, recipes]);

  const prefs = useMemo(() => readPrefs(), []);
  const [secR, setSecR] = useState(!!initial.recipes && recipes.length > 0);
  const [secP, setSecP] = useState(!!initial.prices && rows.length > 0);
  const [tab, setTab] = useState(initial.recipes !== false && recipes.length ? 'recipes' : 'prices');
  const [exR, setExR] = useState(() => new Set(prefs?.exR || []));
  // 처음엔 반제품 줄은 빼 둔다 (레시피로 만드는 것이라 단가표 종이에는 보통 필요 없다)
  const [exP, setExP] = useState(() => new Set(prefs?.exP || rows.filter(p => p.category === '반제품').map(priceKey)));
  const [layout, setLayout] = useState(prefs?.layout === 'flow' ? 'flow' : 'page');
  const [withPrice, setWithPrice] = useState(prefs?.withPrice !== false);
  const [q, setQ] = useState('');

  const qs = useMemo(() => [...new Set(searchVariants(q).map(normalizeIngredientName).filter(Boolean))], [q]);
  const hit = (...vals) => !qs.length || vals.some(v => { const k = normalizeIngredientName(v); return !!k && qs.some(x => k.includes(x)); });

  const selR = recipes.filter(r => !exR.has(r.id));
  const selP = rows.filter(p => !exP.has(priceKey(p)));
  const nR = secR ? selR.length : 0;
  const nP = secP ? selP.length : 0;

  const toggleSet = (setter, keys, on) => setter(prev => {
    const n = new Set(prev);
    keys.forEach(k => (on ? n.delete(k) : n.add(k)));   // on = 넣기(빼기 목록에서 지움)
    return n;
  });

  const doPrint = () => {
    if (!nR && !nP) return;
    const ok = printBundle({
      recipes: secR ? selR : [], prices: secP ? selP : [], zhMap, lang, layout, withPrice, money,
      title: `${[nR ? '레시피' : '', nP ? '단가표' : ''].filter(Boolean).join(' · ')} ${[nR ? '配方' : '', nP ? '单价表' : ''].filter(Boolean).join(' · ')}`,
    });
    writePrefs({ exR: [...exR], exP: [...exP], layout, withPrice });
    if (!ok) { alert(Z('팝업이 차단되었습니다. 팝업을 허용해주세요', '弹出窗口被拦截了，请允许弹出窗口')); return; }
    onClose && onClose();
  };

  // ── 모양 ──
  const chk = { width: 17, height: 17, accentColor: '#EA580C', flexShrink: 0 };
  const smallBtn = { padding: '4px 10px', borderRadius: 7, border: '1px solid #D4D4D8', background: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' };
  const tabBtn = (on) => ({ flex: 1, padding: '10px 8px', borderRadius: 10, border: `2px solid ${on ? '#EA580C' : '#E4E4E7'}`,
    background: on ? '#FFF7ED' : '#fff', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 8 });
  const itemRow = { display: 'flex', alignItems: 'center', gap: 7, padding: '5px 6px', borderRadius: 7, cursor: 'pointer', fontSize: 13 };
  const grid = { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0 10px' };

  // 레시피 목록 (카테고리별)
  const recipeList = (
    <div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
        {[['page', Z('레시피마다 한 장 (벽에 붙이기)', '每个配方一页（贴墙上）')], ['flow', Z('이어서 인쇄 (책자 · 종이 절약)', '连续打印（装订成册 · 省纸）')]].map(([k, l]) => (
          <label key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700, cursor: 'pointer',
                                  padding: '6px 10px', borderRadius: 8, border: `1.5px solid ${layout === k ? '#EA580C' : '#E4E4E7'}`, background: layout === k ? '#FFF7ED' : '#fff' }}>
            <input type="radio" name="pp-layout" data-pp-layout={k} checked={layout === k} onChange={() => setLayout(k)} style={chk} />{l}
          </label>
        ))}
      </div>
      {(categories || []).map((c, ci) => {
        const list = ((c && c.items) || []).filter(r => r && r.id && r.name && hit(r.name, r.name_zh, r.recipeCode));
        if (!list.length) return null;
        const on = list.filter(r => !exR.has(r.id)).length;
        return (
          <div key={ci} style={{ marginBottom: 10 }}>
            <label style={{ ...itemRow, background: '#F4F4F5', fontWeight: 800, fontSize: 12.5 }}>
              <input type="checkbox" data-pp-rcat={c.name} style={chk} checked={on === list.length}
                ref={el => { if (el) el.indeterminate = on > 0 && on < list.length; }}
                onChange={e => toggleSet(setExR, list.map(r => r.id), e.target.checked)} />
              {zh ? (c.name_zh || c.name) : c.name}
              <span style={{ fontWeight: 600, color: '#71717A' }}>{on}/{list.length}</span>
            </label>
            <div style={grid}>
              {list.map(r => (
                <label key={r.id} style={itemRow}>
                  <input type="checkbox" data-pp-r={r.id} style={chk} checked={!exR.has(r.id)} onChange={e => toggleSet(setExR, [r.id], e.target.checked)} />
                  <span style={{ color: exR.has(r.id) ? '#A1A1AA' : '#18181B', textDecoration: exR.has(r.id) ? 'line-through' : 'none' }}>
                    {zh ? (r.name_zh || r.name) : r.name}
                    {(zh ? r.name : r.name_zh) && <span style={{ color: '#1A56A0', fontSize: 11, marginLeft: 5 }}>{zh ? r.name : r.name_zh}</span>}
                  </span>
                </label>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );

  // 단가표 목록 (발주처별)
  const vendorGroups = useMemo(() => {
    const m = new Map();
    for (const p of rows) {
      const v = String(p.vendor || '').trim() || '';
      if (!m.has(v)) m.set(v, []);
      m.get(v).push(p);
    }
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0], 'ko'));
  }, [rows]);
  const priceList = (
    <div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
          <input type="checkbox" data-pp-withprice style={chk} checked={withPrice} onChange={e => setWithPrice(e.target.checked)} />
          {Z('단가 넣기', '打印单价')}
        </label>
        <span style={{ color: '#D4D4D8' }}>|</span>
        {PRICE_CATS.filter(cat => rows.some(p => p.category === cat)).map(cat => {
          const list = rows.filter(p => p.category === cat);
          const on = list.filter(p => !exP.has(priceKey(p))).length;
          return (
            <label key={cat} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
                                      padding: '4px 9px', borderRadius: 999, border: '1.5px solid #E4E4E7', background: on ? '#fff' : '#F4F4F5' }}>
              <input type="checkbox" data-pp-cat={cat} style={chk} checked={on === list.length}
                ref={el => { if (el) el.indeterminate = on > 0 && on < list.length; }}
                onChange={e => toggleSet(setExP, list.map(priceKey), e.target.checked)} />
              {zh ? CAT_ZH[cat] : cat} <span style={{ color: '#71717A', fontWeight: 600 }}>{on}/{list.length}</span>
            </label>
          );
        })}
      </div>
      {vendorGroups.map(([v, list0]) => {
        const list = list0.filter(p => hit(p.name, p.name_zh, p.code, p.vendor));
        if (!list.length) return null;
        const on = list.filter(p => !exP.has(priceKey(p))).length;
        const vz = list0.map(p => String(p.vendor_zh || '').trim()).find(Boolean);
        return (
          <div key={v || '_'} style={{ marginBottom: 10 }}>
            <label style={{ ...itemRow, background: '#F4F4F5', fontWeight: 800, fontSize: 12.5 }}>
              <input type="checkbox" data-pp-vendor={v} style={chk} checked={on === list.length}
                ref={el => { if (el) el.indeterminate = on > 0 && on < list.length; }}
                onChange={e => toggleSet(setExP, list.map(priceKey), e.target.checked)} />
              🏪 {v ? (zh && vz ? vz : v) : Z('발주처 미지정', '未指定供应商')}
              <span style={{ fontWeight: 600, color: '#71717A' }}>{on}/{list.length}</span>
            </label>
            <div style={grid}>
              {list.map(p => {
                const k = priceKey(p);
                const off = exP.has(k);
                return (
                  <label key={k} style={itemRow}>
                    <input type="checkbox" data-pp-p={k} style={chk} checked={!off} onChange={e => toggleSet(setExP, [k], e.target.checked)} />
                    <span style={{ flex: 1, color: off ? '#A1A1AA' : '#18181B', textDecoration: off ? 'line-through' : 'none' }}>
                      {zh ? (p.name_zh || p.name) : p.name}
                      {(zh ? p.name : p.name_zh) && <span style={{ color: '#1A56A0', fontSize: 11, marginLeft: 5 }}>{zh ? p.name : p.name_zh}</span>}
                    </span>
                    <span style={{ fontSize: 11, color: '#A1A1AA' }}>{zh ? (CAT_ZH[p.category] || p.category || '') : (p.category || '')}</span>
                  </label>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );

  const allKeys = tab === 'recipes' ? recipes.filter(r => hit(r.name, r.name_zh, r.recipeCode)).map(r => r.id)
    : rows.filter(p => hit(p.name, p.name_zh, p.code, p.vendor)).map(priceKey);

  return (
    <div data-print-picker style={{ position: 'fixed', inset: 0, zIndex: 1100, background: 'rgba(0,0,0,0.45)', display: 'flex',
                                    alignItems: isMobile ? 'stretch' : 'center', justifyContent: 'center', padding: isMobile ? 0 : 16 }}>
      <div style={{ background: '#fff', borderRadius: isMobile ? 0 : 16, width: '100%', maxWidth: 820, maxHeight: isMobile ? '100%' : '92vh',
                    display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ padding: '16px 18px 10px', borderBottom: '1px solid #F4F4F5' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ flex: 1, fontSize: 18, fontWeight: 900 }}>🖨 {Z('인쇄할 것 고르기', '选择要打印的内容')}</div>
            <button type="button" data-pp-close onClick={onClose} style={{ border: 'none', background: 'none', fontSize: 22, color: '#A1A1AA', cursor: 'pointer' }}>✕</button>
          </div>
          <div style={{ fontSize: 12.5, color: '#71717A', marginTop: 2 }}>
            {Z('체크를 풀면 그 항목은 빼고 인쇄해요. 고른 것은 다음 인쇄 때도 그대로예요.', '取消勾选的项目不打印。下次打印时会记住这次的选择。')}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            {[['recipes', secR, setSecR, Z('레시피', '配方'), selR.length, recipes.length], ['prices', secP, setSecP, Z('단가표', '单价表'), selP.length, rows.length]].map(([k, on, set, label, n, total]) => (
              <div key={k} data-pp-tab={k} onClick={() => setTab(k)} style={tabBtn(tab === k)}>
                <input type="checkbox" data-pp-sec={k} style={chk} checked={on} disabled={!total}
                  onClick={e => e.stopPropagation()} onChange={e => { set(e.target.checked); if (e.target.checked) setTab(k); }} />
                <span style={{ fontSize: 14, fontWeight: 800, color: on ? '#18181B' : '#A1A1AA' }}>{label}</span>
                <span style={{ fontSize: 12, color: '#71717A' }}>{n}/{total}</span>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 6, marginTop: 10, alignItems: 'center' }}>
            <input data-pp-search value={q} onChange={e => setQ(e.target.value)} placeholder={Z('🔍 찾기', '🔍 搜索')}
              style={{ flex: 1, minWidth: 0, padding: '7px 10px', borderRadius: 8, border: '1.5px solid #E4E4E7', fontSize: 13 }} />
            <button type="button" data-pp-all style={smallBtn} onClick={() => toggleSet(tab === 'recipes' ? setExR : setExP, allKeys, true)}>{Z('모두 넣기', '全选')}</button>
            <button type="button" data-pp-none style={smallBtn} onClick={() => toggleSet(tab === 'recipes' ? setExR : setExP, allKeys, false)}>{Z('모두 빼기', '全不选')}</button>
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 18px', opacity: (tab === 'recipes' ? secR : secP) ? 1 : 0.45 }}>
          {tab === 'recipes' ? recipeList : priceList}
        </div>

        <div style={{ padding: '12px 18px', borderTop: '1px solid #F4F4F5', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <div data-pp-summary style={{ flex: 1, fontSize: 13, color: '#52525B', minWidth: 160 }}>
            {Z(`레시피 ${nR}개 · 단가표 ${nP}품목`, `配方 ${nR} 个 · 单价表 ${nP} 项`)}
          </div>
          <button type="button" onClick={onClose} style={{ ...smallBtn, padding: '9px 16px', fontSize: 13 }}>{Z('취소', '取消')}</button>
          <button type="button" data-pp-print disabled={!nR && !nP} onClick={doPrint}
            style={{ padding: '9px 18px', borderRadius: 9, border: 'none', fontSize: 14, fontWeight: 800,
                     background: nR || nP ? '#EA580C' : '#E4E4E7', color: '#fff', cursor: nR || nP ? 'pointer' : 'default' }}>
            🖨 {Z('인쇄', '打印')}
          </button>
        </div>
      </div>
    </div>
  );
}
